/**
 * Papel do endereco que o visitante abriu.
 *
 * A plataforma responde por quatro tipos de host, todos servidos pelo mesmo
 * bundle:
 *
 * | Host                        | Papel            | O que abre                    |
 * |-----------------------------|------------------|-------------------------------|
 * | `imobx.seulugar.imb.br`     | `tenant`         | site e painel da imobiliaria  |
 * | `app.seulugar.imb.br`       | `root`           | imobiliaria original          |
 * | `lp.seulugar.imb.br`        | `landing`        | captacao e cadastro           |
 * | `cliente.seulugar.imb.br`   | `client-gateway` | porta de entrada do cliente   |
 * | `www.imobx.com.br`          | `custom`         | site da imobiliaria (#538)    |
 *
 * Este arquivo e o unico lugar que olha para `window.location.hostname`. Todo o
 * resto pergunta o papel, nao o host — e por isso trocar `lp` por outro nome, ou
 * rodar a plataforma em outro dominio, e mudanca de configuracao, nao de codigo.
 *
 * As variaveis sao lidas a cada chamada, e nao no topo do modulo, para que o
 * teste consiga simular cada ambiente sem recarregar o modulo.
 */

import { getCustomHostTenant } from './custom-host'
import {
  CLIENT_GATEWAY_LABEL,
  isReservedSegment,
  LANDING_LABEL
} from './reserved'

export type HostRole =
  | 'tenant'
  | 'root'
  | 'landing'
  | 'client-gateway'
  | 'custom'

export type TenantMode = 'dual' | 'subdomain' | 'path'

/**
 * Dominios abaixo dos quais cada imobiliaria tem seu subdominio, na ordem de
 * preferencia (`seulugar.imb.br,correttoapp.com.br`).
 *
 * Mais de um dominio serve para a troca de endereco: durante o espelho, a
 * mesma imobiliaria responde em `imobx.seulugar.imb.br` e em
 * `imobx.correttoapp.com.br`.
 *
 * Vazio significa que o ambiente ainda resolve a imobiliaria pelo caminho — o
 * modelo antigo, que continua valendo em desenvolvimento local.
 */
export function baseDomains(): string[] {
  return splitHosts(process.env.REACT_APP_TENANT_BASE_DOMAIN || '')
}

/**
 * Dominio base do endereco atual, ou o primeiro da lista quando o visitante
 * esta fora de todos (localhost, por exemplo).
 *
 * E o que mantem o visitante no dominio em que entrou: quem abriu
 * `app.correttoapp.com.br` recebe links em `correttoapp.com.br`.
 */
export function baseDomain(hostname?: string): string {
  const domains = baseDomains()
  const host = (hostname ?? currentHostname()).toLowerCase()
  return (
    domains.find((base) => host === base || host.endsWith(`.${base}`)) ||
    domains[0] ||
    ''
  )
}

export function tenantMode(): TenantMode {
  const mode = (process.env.REACT_APP_TENANT_MODE || 'dual')
    .toLowerCase()
    .trim()
  return mode === 'subdomain' || mode === 'path' ? mode : 'dual'
}

/** Hosts que significam "a imobiliaria original", e nao uma imobiliaria qualquer. */
export function rootHosts(): string[] {
  return splitHosts(process.env.REACT_APP_TENANT_ROOT_HOSTS || 'localhost')
}

/**
 * Host canonico da imobiliaria original, para montar links absolutos: o que
 * fica no dominio atual, senao o primeiro da lista.
 */
export function rootHost(): string {
  const hosts = rootHosts()
  const base = baseDomain()
  return (
    (base && hosts.find((host) => host.endsWith(`.${base}`))) || hosts[0] || ''
  )
}

/**
 * Hosts sem rotulo que abrem a landing, como a raiz `correttoapp.com.br` e o
 * `www`. O `lp.{dominio}` continua valendo pelo rotulo.
 */
export function landingHosts(): string[] {
  return splitHosts(process.env.REACT_APP_LANDING_HOSTS || '')
}

/** `true` quando o ambiente resolve a imobiliaria pelo subdominio. */
export function usesSubdomain(): boolean {
  return Boolean(baseDomain()) && tenantMode() !== 'path'
}

function currentHostname(): string {
  return typeof window !== 'undefined' ? window.location.hostname : ''
}

function splitHosts(value: string): string[] {
  return value
    .split(',')
    .map((host) => host.toLowerCase().trim())
    .filter(Boolean)
}

/**
 * Primeiro rotulo do host, quando o restante e exatamente o dominio base.
 *
 * `imobx.seulugar.imb.br` (ou `imobx.correttoapp.com.br`) devolve `imobx`;
 * `seulugar.imb.br`, `outro.com` e `a.b.seulugar.imb.br` devolvem `null` — o
 * ultimo de proposito, porque o wildcard de DNS tambem so cobre um nivel.
 */
export function hostLabel(hostname?: string): string | null {
  const host = (hostname ?? currentHostname()).toLowerCase()
  const base = baseDomain(host)
  if (!base) return null

  const suffix = `.${base}`

  if (!host.endsWith(suffix)) return null

  const label = host.slice(0, -suffix.length)
  if (!label || label.includes('.')) return null

  return label
}

/**
 * `true` para o dominio proprio de uma imobiliaria (Feature #538).
 *
 * So `www.` conta (decisao D1 do plano 538), e nunca um host da plataforma.
 * A exigencia do `www.` e o que protege os outros ambientes: homologacao,
 * `localhost` e previews da Vercel nao comecam assim, e por isso nunca passam
 * pela consulta ao back-end nem pela tela de "site nao encontrado".
 */
export function isCustomDomainHost(hostname?: string): boolean {
  const host = (hostname ?? currentHostname()).toLowerCase()

  if (!host.startsWith('www.')) return false
  if (rootHosts().includes(host) || landingHosts().includes(host)) return false

  return !baseDomains().some(
    (base) => host === base || host.endsWith(`.${base}`)
  )
}

export function getHostRole(hostname?: string): HostRole {
  const host = (hostname ?? currentHostname()).toLowerCase()

  if (rootHosts().includes(host)) return 'root'
  if (landingHosts().includes(host)) return 'landing'
  if (isCustomDomainHost(host)) return 'custom'

  const label = hostLabel(host)
  if (!label) return 'root'

  if (label === LANDING_LABEL) return 'landing'
  if (label === CLIENT_GATEWAY_LABEL) return 'client-gateway'
  if (isReservedSegment(label)) return 'root'

  return 'tenant'
}

/**
 * Slug da imobiliaria quando ele vem do host.
 *
 * Devolve `null` — e nao o rotulo — sempre que o host nao for de uma
 * imobiliaria: raiz, landing, porta de entrada, ou ambiente que ainda resolve
 * pelo caminho.
 */
export function slugFromHost(hostname?: string): string | null {
  // O dominio proprio vale em qualquer modo: quem decide o slug e o back-end,
  // consultado antes de a aplicacao montar.
  if (getHostRole(hostname) === 'custom') {
    return getCustomHostTenant()?.slug ?? null
  }

  if (!usesSubdomain()) return null
  return getHostRole(hostname) === 'tenant' ? hostLabel(hostname) : null
}
